<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    class="group"
    @click="emit('click')"
  >
    <!-- Icon -->
    <slot>
      <span
        v-if="icon"
        :class="`inline-flex items-center ${iconHoverClass}`"
        v-html="iconWithSizeClass"
      ></span>
    </slot>

    <!-- Label for screen readers -->
    <span v-if="label" class="sr-only">{{ label }}</span>
  </button>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const emit = defineEmits(["click"]);
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    icon?: string | null; // SVG string or null
    type?: "Primary" | "Secondary" | "Alert";
    size?: "small" | "medium" | "large"; // Sizes for FAB
    label?: string; // Text for screen readers
  }>(),
  {
    disabled: false,
    type: "Primary",
    size: "medium"
  }
);

const buttonClasses = computed(() => {
  let baseClasses =
    "font-medium text-center rounded-full inline-flex items-center justify-center transition-all duration-300 ease-in-out";

  // Size classes
  const sizeClasses = {
    small: "p-2.5 text-sm",
    medium: "p-3.5 text-base",
    large: "p-4 text-lg"
  };
  baseClasses += ` ${sizeClasses[props.size || "medium"]}`;

  if (props.disabled) {
    baseClasses +=
      " bg-gray-200 text-gray-500 cursor-not-allowed opacity-50 dark:bg-gray-700 dark:text-gray-500";
  } else {
    if (props.type === "Primary") {
      baseClasses +=
        " text-white bg-blue-600 hover:bg-blue-700 focus-visible:ring-4 focus-visible:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus-visible:ring-blue-800";
    } else if (props.type === "Secondary") {
      baseClasses +=
        " text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus-visible:ring-4 focus-visible:ring-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:focus-visible:ring-gray-700";
    } else if (props.type === "Alert") {
      baseClasses +=
        " text-white bg-red-600 hover:bg-red-700 focus-visible:ring-4 focus-visible:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus-visible:ring-red-900";
    }
  }

  baseClasses += " active:scale-95";

  return baseClasses;
});

const iconSizeClass = computed(() => {
  const sizeMap = {
    small: "w-4 h-4",
    medium: "w-5 h-5",
    large: "w-6 h-6"
  };
  return sizeMap[props.size || "medium"];
});

const iconHoverClass = computed(() => {
  if (props.type === "Secondary" && !props.disabled) {
    return "group-hover:text-[#1A56DB]";
  }
  return "";
});

const iconWithSizeClass = computed(() =>
  props.icon
    ? props.icon.replace(
        /class="([^"]*)"/,
        `class="text-current ${iconSizeClass.value}"`
      )
    : ""
);
</script>

<style scoped>
/* Focus-visible ensures focus rings only appear for keyboard focus */
button:focus {
  outline: none;
}
button:focus-visible {
  outline: none;
}
</style>
